import { clamp, clamp01, makeRng, smoothstep } from '../core/math';
import type { Side } from '../pose/landmarks';
import type { PunchType, Target } from '../recog/events';
import type { AiPosture, FighterVitals, IncomingAttack, MatchMode } from '../sim/types';
import { Blackboard, type Belief } from './blackboard';
import type { DifficultyProfile } from './difficulty';
import type { ComboScript, Personality } from './personality';

/**
 * AI ARCHITECTURE — why utility-based, with a commitment FSM on top
 * =================================================================
 *
 * Considered:
 *
 *  FSM alone — cheap and debuggable, but boxing decisions are graded, not
 *    categorical: "should I counter or reset?" depends on range AND stamina AND
 *    how committed they are AND how well the last exchange went. Encoding that
 *    in transitions produces a combinatorial mess of states, and every new
 *    personality multiplies it.
 *
 *  Behaviour Tree — great for *sequences* (a scripted three-punch combination is
 *    literally a BT sequence node) but priority-ordered selectors are the wrong
 *    shape for "seven roughly-equal options, pick the best right now". You end up
 *    smuggling utility scoring into the conditions anyway.
 *
 *  GOAP — overkill and actively wrong here. GOAP plans a chain of actions toward
 *    a goal state, which assumes the world holds still while you execute the
 *    plan. In a fight the world invalidates your plan every 200 ms. You would
 *    spend the CPU replanning and throw away the plan each time.
 *
 *  Reinforcement learning — the "obvious" modern answer and the wrong one. No
 *    training environment exists (the player is the environment), the resulting
 *    policy is not authorable ("make him headhunt more in round 3" is impossible),
 *    and a trained policy converges on optimal play, which is not the same thing
 *    as fun or readable play. Also: multi-megabyte weights in a PWA.
 *
 * CHOSEN: Utility AI for *selection*, a tiny FSM for *commitment*, and BT-style
 * scripted sequences for *combinations*.
 *
 *   - Utility handles the graded, many-option decision naturally, and a
 *     personality becomes a weight vector rather than a new subtree. Adding an
 *     archetype is data, not code.
 *   - The commitment FSM enforces the single most important realism constraint:
 *     once a punch is thrown it cannot be recalled. Without it, a utility AI
 *     re-evaluates every tick and produces twitchy, inhuman flickering between
 *     actions — the classic failure mode.
 *   - Scripted combos give the player something *learnable*. A pure per-punch
 *     utility AI produces statistically reasonable but rhythmically formless
 *     output that no one can read, and reading your opponent is the game.
 *
 * Decisions are made at ~12 Hz, not per simulation tick. Humans do not
 * re-evaluate their tactics 120 times a second, and the lower rate is both
 * cheaper and more human-looking.
 */

export type AiState = 'idle' | 'windup' | 'strike' | 'recover' | 'evade' | 'block' | 'move' | 'stunned';

export type AiActionKind =
  | 'hold'
  | 'attack'
  | 'feint'
  | 'step-in'
  | 'step-out'
  | 'circle-left'
  | 'circle-right'
  | 'block'
  | 'slip-left'
  | 'slip-right'
  | 'roll'
  | 'duck'
  | 'pull';

const WINDUP_MS: Record<PunchType, number> = { jab: 85, cross: 115, hook: 140, uppercut: 130, overhand: 180 };
const TRAVEL_MS: Record<PunchType, number> = { jab: 90, cross: 105, hook: 125, uppercut: 110, overhand: 145 };
const POWER: Record<PunchType, number> = { jab: 0.55, cross: 1.0, hook: 1.05, uppercut: 1.1, overhand: 1.25 };

/** Extra wind-up in punch-out mode so the tell is long enough to react to. */
const PUNCH_OUT_BASE_TELL = 220;

export interface AiContext {
  now: number;
  /** True gap to the player, sw — used for physics, never for decisions. */
  gap: number;
  lateral: number;
  /** In punch-out mode the AI does not move: fixed range, upper body only. */
  mode: MatchMode;
  /** True while the opponent is in a punish window and should not be defending. */
  open: boolean;
}

export interface AiSnapshot {
  state: AiState;
  posture: AiPosture;
  ring: { x: number; z: number };
  vitals: FighterVitals;
  /** 0..1 per glove — how high the AI is carrying its hands right now. */
  guard: { left: number; right: number };
  /** Currently telegraphing this punch, for the renderer's wind-up animation. */
  telegraphing: { type: PunchType; side: Side; target: Target; progress: number } | null;
  /** Debug: the last decision and its score. */
  lastDecision: string;
  /** How predictable the AI currently believes the player to be, 0..1. */
  read: number;
}

let attackId = 1;

export class AiBoxer {
  state: AiState = 'idle';
  posture: AiPosture = 'guard-high';
  ring = { x: 0, z: 0 };
  vitals: FighterVitals = { poise: 100, stamina: 100, downs: 0 };
  guard = { left: 1, right: 1 };

  readonly blackboard: Blackboard;
  private rng: () => number;

  private stateUntil = 0;
  private nextDecisionAt = 0;
  private pendingAttack: IncomingAttack | null = null;
  private comboQueue: ComboScript['seq'] = [];
  private comboTempo = 200;
  private lastDecision = 'init';
  private lastAttackAt = -1e6;
  private lastComboNotation = '';
  private recentActions: AiActionKind[] = [];
  /** Set when the AI has deliberately made a mistake this exchange. */
  private mistakeActive = false;
  private momentum = 0;

  onAttack: ((a: IncomingAttack) => void) | null = null;

  constructor(
    public personality: Personality,
    public difficulty: DifficultyProfile,
    seed = 1337,
  ) {
    this.rng = makeRng(seed);
    this.blackboard = new Blackboard(difficulty.reactionMs, difficulty.reactionJitterMs, this.rng);
    this.applyGuardStyle();
  }

  setDifficulty(d: DifficultyProfile): void {
    this.difficulty = d;
    this.blackboard.setReaction(this.effectiveReaction(), d.reactionJitterMs);
  }

  setPersonality(p: Personality): void {
    this.personality = p;
    this.applyGuardStyle();
  }

  reset(): void {
    this.state = 'idle';
    this.ring = { x: 0, z: 0 };
    this.vitals = { poise: 100, stamina: 100, downs: 0 };
    this.comboQueue = [];
    this.pendingAttack = null;
    this.blackboard.clear();
    this.momentum = 0;
  }

  private applyGuardStyle(): void {
    switch (this.personality.guardStyle) {
      case 'philly-shell':
        // Lead hand low by design — an authored, exploitable opening.
        this.guard = { left: 0.45, right: 1 };
        break;
      case 'long-guard':
        this.guard = { left: 0.85, right: 0.9 };
        break;
      case 'peekaboo':
        this.guard = { left: 1, right: 1 };
        break;
      default:
        this.guard = { left: 0.95, right: 0.95 };
    }
  }

  /** Fatigue slows perception, which is both realistic and a fair way to fade. */
  private effectiveReaction(): number {
    const tired = 1 - clamp01(this.vitals.stamina / 100);
    return this.difficulty.reactionMs + 90 * tired * tired;
  }

  /** Player is hurting the AI: raises defensive utility, lowers aggression. */
  registerHit(quality: number, clean: boolean): void {
    const dmg = quality * (clean ? 9 : 3.5);
    this.vitals.poise = Math.max(0, this.vitals.poise - dmg);
    this.momentum = clamp(this.momentum - quality * 0.6, -1, 1);
    if (clean && quality > 0.75 && this.rng() < 0.35) {
      this.state = 'stunned';
      this.stateUntil = this.blackboard.belief.t + 320 + 260 * quality;
      this.posture = 'guard-tight';
    }
  }

  registerLanded(quality: number): void {
    this.momentum = clamp(this.momentum + quality * 0.5, -1, 1);
  }

  snapshot(): AiSnapshot {
    let telegraphing: AiSnapshot['telegraphing'] = null;
    if (this.state === 'windup' && this.pendingAttack) {
      const a = this.pendingAttack;
      const total = Math.max(1, a.tImpact - a.tTelegraph);
      telegraphing = {
        type: a.type,
        side: a.side,
        target: a.target,
        progress: clamp01((this.blackboard.belief.t - a.tTelegraph) / total),
      };
    }
    return {
      state: this.state,
      posture: this.posture,
      ring: { ...this.ring },
      vitals: { ...this.vitals },
      guard: { ...this.guard },
      telegraphing,
      lastDecision: this.lastDecision,
      read: this.blackboard.model.predictability,
    };
  }

  update(dtMs: number, ctx: AiContext): void {
    const now = ctx.now;
    this.blackboard.setReaction(this.effectiveReaction(), this.difficulty.reactionJitterMs);
    this.blackboard.update(now);

    this.tickVitals(dtMs);
    this.tickMovement(dtMs, ctx);

    // Commitment: while winding up, striking or recovering, the decision is made.
    if (now < this.stateUntil) {
      this.tickCommitted(now);
      return;
    }

    switch (this.state) {
      case 'windup':
        this.state = 'strike';
        this.stateUntil = now + (this.pendingAttack ? this.pendingAttack.tRecover - this.pendingAttack.tImpact : 120);
        return;
      case 'strike':
        this.state = 'recover';
        this.stateUntil = now + this.recoveryMs();
        this.posture = this.mistakeActive ? 'neutral' : 'guard-high';
        return;
      case 'recover':
        this.mistakeActive = false;
        this.applyGuardStyle();
        // Chain the next punch of a scripted combination without re-deciding.
        if (this.comboQueue.length > 0) {
          this.launchNext(now, ctx);
          return;
        }
        this.state = 'idle';
        this.posture = 'guard-high';
        break;
      case 'evade':
      case 'block':
      case 'move':
      case 'stunned':
        this.state = 'idle';
        this.posture = 'guard-high';
        break;
    }

    if (now < this.nextDecisionAt) return;
    this.nextDecisionAt = now + 70 + this.rng() * 45;
    this.decide(now, ctx);
  }

  private tickCommitted(now: number): void {
    if (this.state === 'windup' && this.pendingAttack && now >= this.pendingAttack.tImpact) {
      this.state = 'strike';
      this.stateUntil = this.pendingAttack.tRecover;
    }
  }

  private tickVitals(dtMs: number): void {
    const s = dtMs / 1000;
    const regen = this.state === 'idle' || this.state === 'move' ? 3.2 : 0.6;
    this.vitals.stamina = clamp(this.vitals.stamina + (regen - 0.4) * s * this.difficulty.conditioning, 0, 100);
    this.vitals.poise = clamp(this.vitals.poise + 2.6 * s, 0, 100);

    // Fatigue expresses itself the way the archetype fights: sluggers drop their
    // hands, out-boxers just stop punching.
    const tired = 1 - clamp01(this.vitals.stamina / 100);
    const guardSag = tired * this.personality.fatigueGuardBias * 0.55;
    if (this.state === 'idle') {
      this.guard.left = clamp01(this.guard.left - guardSag * 0.02);
      this.guard.right = clamp01(this.guard.right - guardSag * 0.02);
    }
  }

  private tickMovement(dtMs: number, ctx: AiContext): void {
    // Punch-out: the fighters are locked at a fixed distance. Nothing here is
    // measurable from a phone camera in a bedroom, so nothing here is simulated.
    if (ctx.mode === 'punch-out') {
      this.ring.x = 0;
      this.ring.z = 0;
      return;
    }
    const s = dtMs / 1000;
    // Continuous range-holding on top of discrete step actions. Without this the
    // AI teleports between ranges; with it, range feels like a tug of war.
    const want = this.personality.preferredGap;
    const err = ctx.gap - want;
    const drive =
      this.personality.rangeDiscipline * this.difficulty.positioning * clamp(err, -1, 1) * (this.state === 'idle' ? 1 : 0.25);
    const speed = 1.1 * (0.55 + 0.45 * clamp01(this.vitals.stamina / 100));
    this.ring.z += drive * speed * s;
    this.ring.x += (ctx.lateral * 0.35 - this.ring.x) * 1.2 * s;
    this.ring.z = clamp(this.ring.z, -1.2, 1.4);
    this.ring.x = clamp(this.ring.x, -1.5, 1.5);
  }

  private recoveryMs(): number {
    const tired = 1 - clamp01(this.vitals.stamina / 100);
    const base = this.personality.recoveryMs * (1 + 0.45 * tired);
    // A "mistake" is expressed as a long, punishable recovery rather than as an
    // invisible dice roll. The player can see it and take it.
    return this.mistakeActive ? base * 2.1 : base;
  }

  // ---------------------------------------------------------------- decisions

  private decide(now: number, ctx: AiContext): void {
    const punchOut = ctx.mode === 'punch-out';
    const b = this.blackboard.belief;
    const d = this.difficulty;
    const p = this.personality;
    const stamina = clamp01(this.vitals.stamina / 100);
    const poise = clamp01(this.vitals.poise / 100);

    // Believed gap, not true gap: the AI acts on lagged information, so a player
    // who steps out during its wind-up genuinely escapes.
    const gap = b.gap;
    const gapErr = gap - p.preferredGap;

    // --- Considerations (each 0..1) ----------------------------------------
    // At fixed range you are always in range and never out of position.
    const inRange = punchOut ? 1 : 1 - smoothstep(0.25, 0.95, Math.abs(gapErr));
    const tooFar = punchOut ? 0 : smoothstep(0.15, 0.9, gapErr);
    const tooClose = punchOut ? 0 : smoothstep(0.15, 0.9, -gapErr);
    const threat = b.playerCommitted ? 1 : 1 - smoothstep(250, 900, b.sinceOnset);
    // The counter window: the player has just finished punching and their hand
    // is still coming home. Highest-value moment in the fight.
    const counterWindow = b.sinceRecovery < 340 ? 1 - smoothstep(80, 340, b.sinceRecovery) : 0;
    const guardOpen = b.guard ? 1 - Math.min(b.guard.left, b.guard.right) : 0.2;
    const freshness = 1 - smoothstep(180, 900, now - this.lastAttackAt);
    // Momentum modulates aggression but must never zero it. Without the floor, a
    // fighter who takes a beating drives momentum to -1, stops punching entirely,
    // and can then never regain momentum (which only rises from landing) — a
    // death spiral where the opponent simply stands there for the rest of the
    // round. Even a badly-hurt boxer throws something back.
    const aggression = clamp(p.aggression + 0.28 * this.momentum, p.aggression * 0.4, 1) * d.outputRate;

    // Anticipation: if the model is confident, treat a *predicted* punch as if it
    // were already perceived. This is where high difficulty gets its speed from
    // without going below human reaction time.
    const predictionStrength = b.prediction.p * d.anticipation;
    const anticipatedThreat = clamp01(Math.max(threat, predictionStrength));

    const scores: Array<[AiActionKind, number]> = [];
    const push = (k: AiActionKind, v: number) => scores.push([k, Math.max(0, v)]);

    // Standing still is only attractive when you are already where you want to
    // be. Without the `inRange` gate, a badly-positioned fighter would rather
    // idle than walk, and simply never re-engage.
    push('hold', (0.14 + 0.28 * (1 - aggression) + 0.3 * (1 - stamina)) * (0.25 + 0.75 * inRange));

    // Offence
    const attackScore =
      inRange *
      aggression *
      // Punch-out lives or dies on the opponent giving the player something to
      // react to. Attacking is therefore the default behaviour, not one option
      // among many, and `pressure` is the tier's output dial.
      (punchOut ? 1.9 * d.pressure : 1) *
      // Caught with the hands down: stop trying to punch and cover up.
      (ctx.open ? 0.05 : 1) *
      (0.35 + 0.65 * stamina) *
      // The counter window dominates the offence decision on purpose. A boxer who
      // throws on a neutral beat and one who waits for your hand to come back are
      // different fighters, and this coefficient is most of that difference.
      (0.55 + 1.3 * counterWindow) *
      (0.7 + 0.5 * guardOpen) *
      (1 - 0.45 * freshness) *
      (0.5 + 0.5 * poise);
    push('attack', attackScore);
    push('feint', attackScore * p.feintRate * 2.2 * (1 - counterWindow));

    // Defence — gated by `defensiveCoverage`, which is what actually separates a
    // low-tier opponent from a high-tier one once reaction time is floored.
    // While open, the opponent is caught out of position and cannot defend. This
    // is what makes the punish window a real window rather than a suggestion:
    // it is not that the player's punches are buffed, it is that the opponent
    // genuinely stops defending itself.
    const defensiveUrge = ctx.open ? 0 : anticipatedThreat * d.defensiveCoverage * (0.5 + 0.5 * (1 - poise));
    const slipTo = this.chooseSlipDirection(b);

    // Choosing *which* evasion, given what we expect to be coming. Only applied
    // once the model is reasonably confident, so a fresh opponent picks
    // generically and a settled one picks correctly — the player should be able
    // to feel it start choosing better as the round goes on.
    const pn = b.prediction.p > 0.35 ? b.prediction.number : 0;
    const expectStraight = pn === 1 || pn === 2;
    const expectHook = pn === 3 || pn === 4;
    const expectUpper = pn === 5 || pn === 6;

    push(
      slipTo === 'left' ? 'slip-left' : 'slip-right',
      defensiveUrge * p.evasionBias * 1.15 * (expectStraight ? 1.35 : expectHook ? 0.7 : 1),
    );
    push('roll', defensiveUrge * p.evasionBias * 0.75 * smoothstep(1.3, 0.7, gap) * (expectHook ? 1.6 : 0.85));
    // Ducking into an uppercut is the classic error. Once it has read that the
    // uppercut is coming, it stops making it — a visible sign it is learning you.
    push('duck', defensiveUrge * p.evasionBias * 0.6 * (expectUpper ? 0.3 : 1));
    push('pull', defensiveUrge * p.evasionBias * 0.5 * smoothstep(0.9, 1.5, gap) * (expectUpper ? 1.5 : 1));
    push('block', defensiveUrge * (1 - p.evasionBias) * 1.25);

    // Movement
    push('step-in', tooFar * p.rangeDiscipline * d.positioning * (0.4 + 0.6 * aggression));
    push('step-out', tooClose * p.rangeDiscipline * d.positioning * (0.5 + 0.5 * (1 - aggression)) + 0.5 * defensiveUrge * (1 - p.evasionBias) * tooClose);
    const circle = punchOut ? 0 : 0.25 * d.positioning * (1 - aggression) + 0.4 * (1 - poise);
    push('circle-left', circle * (b.lateral > 0 ? 1.3 : 0.7));
    push('circle-right', circle * (b.lateral < 0 ? 1.3 : 0.7));

    // Repetition penalty. Utility AI's characteristic failure is picking the same
    // locally-optimal action forever; a short memory penalty is enough to fix it
    // and reads as a fighter "mixing it up".
    //
    // Crucially it is NOT applied to movement that is correcting range. Closing
    // a two-metre gap takes five or six steps in the same direction; penalising
    // repetition there made the AI take two steps, get bored, and stand around
    // out of range for the rest of the round.
    const correcting = new Set<AiActionKind>();
    if (tooFar > 0.4) correcting.add('step-in');
    if (tooClose > 0.4) correcting.add('step-out');
    for (const s of scores) {
      if (correcting.has(s[0])) continue;
      const reps = this.recentActions.filter((a) => a === s[0]).length;
      s[1] *= Math.pow(0.72, reps);
    }

    // Soft-max over the top few rather than a hard argmax. Deterministic argmax
    // makes an AI legible in the bad way: players learn the exact rule instead of
    // the fighting pattern.
    scores.sort((a, b2) => b2[1] - a[1]);
    const top = scores.slice(0, 4).filter((s) => s[1] > 0.02);
    const choice = top.length ? this.softPick(top) : 'hold';

    this.lastDecision = `${choice} (${top.map(([k, v]) => `${k}:${v.toFixed(2)}`).join(' ')})`;
    this.recentActions.push(choice);
    if (this.recentActions.length > 5) this.recentActions.shift();

    this.execute(choice, now, ctx);
  }

  private softPick(top: Array<[AiActionKind, number]>): AiActionKind {
    // Temperature scales with how tired/rattled the AI is: a fresh fighter picks
    // sharply, a hurt one makes looser choices.
    const temp = 0.35 + 0.5 * (1 - clamp01(this.vitals.poise / 100));
    let total = 0;
    const w = top.map(([, v]) => {
      const e = Math.exp(v / temp);
      total += e;
      return e;
    });
    let r = this.rng() * total;
    for (let i = 0; i < top.length; i++) {
      r -= w[i];
      if (r <= 0) return top[i][0];
    }
    return top[0][0];
  }

  /**
   * Which way to move the head.
   *
   * Priority order is deliberate: OBSERVE first, predict only as a fallback.
   *
   *  1. If we have perceived a hand leave the player's guard within the last
   *     ~400 ms, move off that hand's line. A punch travelling from the player's
   *     left arrives on our right, so we slip the other way. This is what makes
   *     the opponent look like it is watching you rather than rolling dice.
   *  2. Otherwise — i.e. we are moving pre-emptively on anticipation alone,
   *     before any hand has moved — fall back to the learned punch-number model.
   *  3. Otherwise coin-flip.
   */
  private chooseSlipDirection(b: Belief): Side {
    if (b.onsetSide && b.sinceOnset < 400) return b.onsetSide === 'left' ? 'right' : 'left';

    const predicted = b.prediction.number;
    if (predicted === 3 || predicted === 5) return 'right';
    if (predicted === 4 || predicted === 6) return 'left';
    return this.rng() < 0.5 ? 'left' : 'right';
  }

  private execute(kind: AiActionKind, now: number, ctx: AiContext): void {
    switch (kind) {
      case 'hold':
        this.state = 'idle';
        this.posture = 'guard-high';
        this.stateUntil = now + 90;
        return;
      case 'attack':
      case 'feint':
        this.beginAttack(now, ctx, kind === 'feint');
        return;
      case 'block':
        this.state = 'block';
        this.posture = 'guard-tight';
        this.guard = { left: 1, right: 1 };
        this.stateUntil = now + 240 + this.rng() * 160;
        return;
      case 'slip-left':
      case 'slip-right':
      case 'roll':
      case 'duck':
      case 'pull': {
        this.state = 'evade';
        this.posture =
          kind === 'slip-left' ? 'slip-left' : kind === 'slip-right' ? 'slip-right' : kind === 'roll' ? 'roll' : kind === 'duck' ? 'duck' : 'pull';
        this.stateUntil = now + 200 + this.rng() * 140;
        this.vitals.stamina -= 1.1 * this.personality.fatigueRate;
        return;
      }
      case 'step-in':
      case 'step-out':
      case 'circle-left':
      case 'circle-right': {
        this.state = 'move';
        this.posture = 'neutral';
        const dz = kind === 'step-in' ? 0.34 : kind === 'step-out' ? -0.34 : 0;
        const dx = kind === 'circle-left' ? -0.3 : kind === 'circle-right' ? 0.3 : 0;
        this.ring.z = clamp(this.ring.z + dz, -1.2, 1.4);
        this.ring.x = clamp(this.ring.x + dx, -1.5, 1.5);
        this.stateUntil = now + 190 + this.rng() * 110;
        this.vitals.stamina -= 0.9 * this.personality.fatigueRate;
        return;
      }
    }
  }

  private beginAttack(now: number, ctx: AiContext, forceFeint: boolean): void {
    // Roll a mistake for this exchange. Expressed as behaviour, never as a stat.
    this.mistakeActive = this.rng() < this.difficulty.mistakeRate;

    // Pick a scripted combination or a single. Combinations rise with aggression
    // and momentum, which is why a fighter who is winning starts stringing shots.
    const useCombo = this.rng() < clamp01(0.25 + 0.5 * this.personality.aggression + 0.3 * this.momentum);
    if (useCombo && !forceFeint) {
      const script = this.pickCombo();
      this.comboQueue = [...script.seq];
      this.comboTempo = script.tempoMs;
      this.lastComboNotation = script.seq.map((s) => s.type[0]).join('');
    } else {
      const s = this.pickSingle();
      this.comboQueue = [s];
      this.comboTempo = 200;
    }
    this.launchNext(now, ctx, forceFeint);
  }

  private launchNext(now: number, ctx: AiContext, forceFeint = false): void {
    const next = this.comboQueue.shift();
    if (!next) {
      this.state = 'idle';
      return;
    }
    const isFeint = forceFeint || (this.comboQueue.length > 0 && this.rng() < this.personality.feintRate * 0.4);

    // The tell. This is the reaction-training stimulus, so its length is a
    // first-class difficulty axis: Beginner gets a 2.1x wind-up (a long, obvious
    // look at the punch), Professional gets 0.62x.
    //
    // PUNCH_OUT_BASE_TELL exists because the free-spar wind-ups are far too
    // short to react to on purpose. A veteran's jab was 115 ms of wind-up plus
    // 90 ms of travel — 205 ms of total warning, which is at or below human
    // choice-reaction time. Every dodge would have been a guess. With the base
    // tell the ladder spans ~790 ms (Beginner) to ~300 ms (Professional), which
    // is the range where reacting is actually a trainable skill.
    const baseTell = ctx.mode === 'punch-out' ? PUNCH_OUT_BASE_TELL : 0;
    const windup =
      (WINDUP_MS[next.type] + this.personality.tellMs + baseTell + (this.mistakeActive ? 45 : 0)) *
      this.difficulty.telegraphScale;
    const travel = TRAVEL_MS[next.type] / clamp(0.75 + 0.35 * clamp01(this.vitals.stamina / 100), 0.7, 1.15);

    // Target the habitual opening if we have a read on one; otherwise pick the
    // side the personality prefers.
    const opening = this.blackboard.belief.opening;
    const lane = this.laneFor(next, opening.side, opening.p);

    const atk: IncomingAttack = {
      id: attackId++,
      type: next.type,
      side: next.lead ? 'left' : 'right',
      target: next.target,
      tTelegraph: now,
      tImpact: now + windup + travel,
      tRecover: now + windup + travel + this.recoveryMs() + (this.comboQueue.length > 0 ? 0 : 60),
      lane,
      height: next.target === 'head' ? -0.05 : -0.85,
      power: POWER[next.type] * (0.7 + 0.3 * clamp01(this.vitals.stamina / 100)),
      isFeint,
      resolved: false,
    };

    this.pendingAttack = atk;
    this.state = 'windup';
    this.stateUntil = atk.tImpact;
    this.posture = 'neutral';
    this.lastAttackAt = now;
    this.vitals.stamina -= (isFeint ? 0.8 : 2.4) * this.personality.fatigueRate * POWER[next.type];

    // Dropping the guard hand to throw is real, and it is the opening the player
    // is supposed to find. Sluggers and tired fighters do it more.
    const throwing: Side = next.lead ? 'left' : 'right';
    const tired = 1 - clamp01(this.vitals.stamina / 100);
    this.guard[throwing] = clamp01(0.35 - 0.25 * tired);
    if (this.mistakeActive) this.guard[throwing === 'left' ? 'right' : 'left'] *= 0.55;

    if (!isFeint) this.onAttack?.(atk);
    // Pace the rest of the combination.
    if (this.comboQueue.length > 0) this.stateUntil = atk.tImpact + Math.max(40, this.comboTempo - travel);
  }

  private laneFor(next: ComboScript['seq'][number], openingSide: Side | null, p: number): number {
    const base = next.type === 'hook' ? (next.lead ? -0.3 : 0.3) : next.type === 'uppercut' ? 0 : next.lead ? -0.12 : 0.12;
    if (!openingSide || p < 0.45) return base;
    // Aim at the hand they habitually drop.
    return openingSide === 'left' ? base - 0.18 : base + 0.18;
  }

  private pickCombo(): ComboScript {
    const combos = this.personality.combos;
    let total = 0;
    for (const c of combos) total += c.weight;
    let r = this.rng() * total;
    for (const c of combos) {
      r -= c.weight;
      if (r <= 0) return c;
    }
    return combos[0];
  }

  private pickSingle(): { type: PunchType; lead: boolean; target: Target } {
    const singles = this.personality.singles;
    let total = 0;
    for (const s of singles) total += s.weight;
    let r = this.rng() * total;
    for (const s of singles) {
      r -= s.weight;
      if (r <= 0) return { type: s.type, lead: s.lead, target: s.target };
    }
    return { type: singles[0].type, lead: singles[0].lead, target: singles[0].target };
  }

  get lastCombo(): string {
    return this.lastComboNotation;
  }
}
